import React, { useRef, useEffect } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { VoiceStatus } from "./useRealtimeVoice";
import styles from "../QuestionInput/QuestionInput.module.css";
import voiceInteractionGif from "../../assets/voice-interaction.gif";
import voiceInteractionGifOrange from "../../assets/voice-interaction-orange.gif";
import giphyGif from "../../assets/giphy.gif";

export const ORB_COLORS: Record<VoiceStatus, string> = {
  idle:        "transparent",
  connecting:  "#9b9b9b",
  listening:   "#27ae60",
  thinking:    "#8e44ad",
  speaking:    "#0ea5e9",
  interrupted: "#f39c12",
  error:       "#e53e3e",
};

const ORB_LABELS: Record<VoiceStatus, string> = {
  idle:        "",
  connecting:  "Connecting…",
  listening:   "Listening…",
  thinking:    "Processing…",
  speaking:    "Speaking — tap to cut in",
  interrupted: "Got it, listening…",
  error:       "Connection error",
};

interface VoiceOrbProps {
  status: VoiceStatus;
  onStop: () => void;
  compact?: boolean;
}

export const VoiceOrb: React.FC<VoiceOrbProps> = ({
  status,
  onStop,
  compact = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef   = useRef<number>(0);
  const isVisible = status !== "idle";
  const color     = ORB_COLORS[status];
  const canvasSize = compact ? 88 : 180;
  const frameWidth = compact ? 148 : 300;
  const frameHeight = compact ? 108 : 200;
  const orbLabelOffset = compact ? "0" : "-10px";
  const orbLabelBottom = compact ? "0" : "10px";
  const containerMaxHeight = compact ? "140px" : "240px";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    let frame = 0;

    const draw = () => {
      frame++;
      const t  = frame * 0.04;
      const W  = canvas.width;
      const H  = canvas.height;
      const cx = W / 2;
      const cy = H / 2;
      ctx.clearRect(0, 0, W, H);

      if (status === "speaking") {
        ctx.beginPath();
        for (let i = 0; i <= 360; i++) {
          const a = (i * Math.PI) / 180;
          const noise =
            Math.sin(a * 3 + t * 1.5) * 12 +
            Math.sin(a * 5 - t * 2.1) * 7  +
            Math.sin(a * 7 + t * 0.9) * 4;
          const r = 52 + noise;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        const g = ctx.createRadialGradient(cx, cy, 20, cx, cy, 80);
        g.addColorStop(0,   "rgba(14,165,233,0.95)");
        g.addColorStop(0.5, "rgba(2,132,199,0.75)");
        g.addColorStop(1,   "rgba(7,89,133,0.15)");
        ctx.fillStyle = g;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 24, 0, Math.PI * 2);
        const gc = ctx.createRadialGradient(cx, cy, 0, cx, cy, 24);
        gc.addColorStop(0, "rgba(255,255,255,0.95)");
        gc.addColorStop(1, "rgba(125,211,252,0.2)");
        ctx.fillStyle = gc;
        ctx.fill();

        const glow = ctx.createRadialGradient(cx, cy, 52, cx, cy, 85);
        glow.addColorStop(0, "rgba(14,165,233,0.18)");
        glow.addColorStop(1, "rgba(14,165,233,0)");
        ctx.beginPath();
        ctx.arc(cx, cy, 85, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

      } else if (status === "listening") {
        const breathe = Math.sin(t * 0.8) * 0.1 + 1;
        const r       = 50 * breathe;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        const g = ctx.createRadialGradient(cx, cy, 8, cx, cy, r);
        g.addColorStop(0,   "rgba(39,174,96,0.95)");
        g.addColorStop(0.5, "rgba(39,174,96,0.65)");
        g.addColorStop(1,   "rgba(39,174,96,0.05)");
        ctx.fillStyle = g;
        ctx.fill();
        for (let ring = 0; ring < 3; ring++) {
          const phase = ((t * 0.35 + ring * 0.55) % 2) / 2;
          const ringR = r + phase * 55;
          const alpha = (1 - phase) * 0.4;
          ctx.beginPath();
          ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(39,174,96,${alpha})`;
          ctx.lineWidth   = 1.5;
          ctx.stroke();
        }

      } else if (status === "thinking") {
        for (let p = 0; p < 6; p++) {
          const a  = (p / 6) * Math.PI * 2 + t * 1.4;
          const px = cx + Math.cos(a) * 42;
          const py = cy + Math.sin(a) * 42;
          const sz = 4.5 + Math.sin(t * 2 + p) * 1.8;
          const al = 0.5 + Math.sin(t * 1.8 + p * 1.2) * 0.3;
          ctx.beginPath();
          ctx.arc(px, py, sz, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(142,68,173,${al + 0.3})`;
          ctx.fill();
        }
        const pScale = Math.abs(Math.sin(t * 1.5)) * 0.35 + 0.65;
        ctx.beginPath();
        ctx.arc(cx, cy, 18 * pScale, 0, Math.PI * 2);
        const gc = ctx.createRadialGradient(cx, cy, 0, cx, cy, 18);
        gc.addColorStop(0, "rgba(210,140,255,0.95)");
        gc.addColorStop(1, "rgba(142,68,173,0.15)");
        ctx.fillStyle = gc;
        ctx.fill();

      } else if (status === "interrupted") {
        const flashPhase = (t % 55) / 55;
        if (flashPhase < 0.28) {
          for (let s = 0; s < 8; s++) {
            const a   = (s / 8) * Math.PI * 2;
            const len = 18 + (flashPhase / 0.28) * 28;
            ctx.beginPath();
            ctx.moveTo(cx + Math.cos(a) * 16, cy + Math.sin(a) * 16);
            ctx.lineTo(cx + Math.cos(a) * (16 + len), cy + Math.sin(a) * (16 + len));
            ctx.strokeStyle = `rgba(243,156,18,${1 - flashPhase / 0.28})`;
            ctx.lineWidth   = 2.5;
            ctx.lineCap     = "round";
            ctx.stroke();
          }
        }
        const r = 40 + Math.sin(t * 0.5) * 4;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, "rgba(243,156,18,0.92)");
        g.addColorStop(1, "rgba(230,126,34,0.15)");
        ctx.fillStyle = g;
        ctx.fill();

      } else if (status === "connecting") {
        for (let arc = 0; arc < 3; arc++) {
          const start = t * 1.2 + (arc * Math.PI * 2) / 3;
          const end   = start + 0.85;
          const r     = 30 + arc * 10;
          ctx.beginPath();
          ctx.arc(cx, cy, r, start, end);
          ctx.strokeStyle = `rgba(155,155,155,${0.9 - arc * 0.22})`;
          ctx.lineWidth   = 3;
          ctx.lineCap     = "round";
          ctx.stroke();
        }

      } else if (status === "error") {
        const pulse = Math.abs(Math.sin(t * 1.5));
        ctx.beginPath();
        ctx.arc(cx, cy, 42 * (0.9 + pulse * 0.1), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(229,62,62,${0.15 + pulse * 0.1})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(229,62,62,${0.75 + pulse * 0.25})`;
        ctx.lineWidth   = 4;
        ctx.lineCap     = "round";
        const off = 14;
        ctx.beginPath(); ctx.moveTo(cx - off, cy - off); ctx.lineTo(cx + off, cy + off); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx + off, cy - off); ctx.lineTo(cx - off, cy + off); ctx.stroke();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [status]);

  return (
    <div
      style={{
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        justifyContent: "center",
        overflow:       "hidden",
        maxHeight:      isVisible ? containerMaxHeight : "0px",
        opacity:        isVisible ? 1 : 0,
        transform:      isVisible ? "translateY(0) scale(1)" : "translateY(14px) scale(0.88)",
        transition:     "max-height 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.35s ease, transform 0.4s cubic-bezier(0.34,1.56,0.64,1)",
        pointerEvents:  isVisible ? "all" : "none",
        marginBottom:   isVisible ? (compact ? "0" : "4px") : "0",
      }}
    >
      <div style={{ position: "relative", width: `${frameWidth}px`, height: `${frameHeight}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            position:     "absolute",
            inset:        "-20px",
            borderRadius: "50%",
            // background:   `radial-gradient(circle, ${color}28 0%, transparent 68%)`,
            filter:       "blur(14px)",
            transition:   "background 0.5s ease",
          }}
        />

        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          style={{
            position:   "relative",
            zIndex:     2,
            filter:     `drop-shadow(0 0 16px ${color}60)`,
            transition: "filter 0.5s ease",
          }}
        />

        <button
          type="button"
          onClick={onStop}
          title="End voice session"
          style={{
            position:       "absolute",
            bottom:         compact ? "10px" : "8px",
            right:          compact ? "10px" : "12px",
            zIndex:         10,
            background:     "rgba(255,255,255,0.12)",
            border:         `1px solid ${color}55`,
            borderRadius:   "50%",
            width:          compact ? "22px" : "26px",
            height:         compact ? "22px" : "26px",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            cursor:         "pointer",
            backdropFilter: "blur(8px)",
            color:          color,
            transition:     "all 0.2s ease",
          }}
        >
          <svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor">
            <rect x="4" y="4" width="16" height="16" rx="2.5" />
          </svg>
        </button>
      </div>

      <div
        style={{
          fontFamily:    "var(--bs-body-font-family, system-ui, sans-serif)",
          fontSize:      compact ? "10px" : "11px",
          fontWeight:    700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color:         color,
          display:       "flex",
          alignItems:    "center",
          gap:           "6px",
          marginTop:     orbLabelOffset,
          marginBottom:  orbLabelBottom,
          transition:    "color 0.4s ease",
        }}
      >
        {status === "listening" && (
          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: color, display: "inline-block", animation: "orbPulse 1.1s ease-in-out infinite" }} />
        )}
        {status === "thinking" && (
          <span style={{ display: "flex", gap: "3px" }}>
            {[0, 1, 2].map((i) => (
              <span key={i} style={{ width: "4px", height: "4px", borderRadius: "50%", background: color, display: "inline-block", animation: `orbBounce 0.85s ease-in-out ${i * 0.14}s infinite` }} />
            ))}
          </span>
        )}
        {status === "speaking" && (
          <span style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "12px" }}>
            {[6, 10, 8, 5].map((h, i) => (
              <span key={i} style={{ width: "3px", height: `${h}px`, borderRadius: "3px", background: color, display: "inline-block", animation: `orbWave 0.55s ease-in-out ${i * 0.1}s infinite alternate` }} />
            ))}
          </span>
        )}
        {ORB_LABELS[status]}
      </div>

      <style>{`
        @keyframes orbPulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.65)} }
        @keyframes orbBounce { 0%,100%{transform:translateY(0);opacity:.45} 50%{transform:translateY(-5px);opacity:1} }
        @keyframes orbWave   { from{transform:scaleY(.4)} to{transform:scaleY(1.5)} }
      `}</style>
    </div>
  );
};

interface MicButtonProps {
  voiceStatus: VoiceStatus;
  onClick:     () => void;
  disabled?:   boolean;
  tooltipTextOverride?: string;
}

const getMicStatusText = (voiceStatus: VoiceStatus): string => {
  switch (voiceStatus) {
    case "connecting":
      return "Connecting";
    case "listening":
      return "Listening";
    case "thinking":
      return "Processing";
    case "speaking":
      return "Speaking";
    case "interrupted":
      return "Interrupted";
    case "error":
      return "Retry";
    default:
      return "";
  }
};

export const MicButton: React.FC<MicButtonProps> = ({
  voiceStatus,
  onClick,
  disabled = false,
  tooltipTextOverride,
}) => {
  const isConnecting = voiceStatus === "connecting";
  const isActive     = !["idle", "error"].includes(voiceStatus);
  const isSpeaking   = voiceStatus === "speaking";
  const isListening  = voiceStatus === "listening" || voiceStatus === "interrupted";
  const isAnimated   = isActive || isConnecting;
  const statusText   = getMicStatusText(voiceStatus);
  const gifSource    = isConnecting ? giphyGif : voiceInteractionGif;

  const tooltipText =
    tooltipTextOverride ? tooltipTextOverride
    : voiceStatus === "idle"       ? "Voice assist"
    : voiceStatus === "connecting" ? "Connecting…"
    : voiceStatus === "error"      ? "Error - click to retry"
    : voiceStatus === "speaking"   ? "Tap to interrupt AI"
    :                                "Listening… click to stop";

  return (
    <>
      <div className={styles.voiceButtonShell}>
        <button
          type="button"
          className={[
            styles.voiceAssButton,
            isActive    ? styles.micActive    : "",
            isSpeaking  ? styles.micSpeaking  : "",
            isListening ? styles.micListening : "",
            disabled    ? styles.micDisabled  : "",
          ].join(" ")}
          onClick={disabled || isConnecting ? undefined : onClick}
          aria-disabled={disabled || isConnecting}
          data-tooltip-id="voiceModel"
        >
          {isActive && <span className={styles.micRipple} />}
          {isAnimated ? (
            <img
              src={gifSource}
              alt=""
              aria-hidden="true"
              className={styles.voiceMicGifActive}
            />
          ) : (
            <i className={styles.voicesvgWrapper}>
              {/* <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="16" width="16" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 7H7V17H5V7ZM1 10H3V14H1V10ZM9 2H11V20H9V2ZM13 4H15V22H13V4ZM17 7H19V17H17V7ZM21 10H23V14H21V10Z" />
              </svg> */}
               <svg height="16" width="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_752_3283)">
                  <path className={styles.voicebar} d="M0.952381 7.61926C0.952381 7.35626 0.739183 7.14307 0.47619 7.14307C0.213198 7.14307 0 7.35626 0 7.61926V12.3812C0 12.6442 0.213198 12.8574 0.47619 12.8574C0.739183 12.8574 0.952381 12.6442 0.952381 12.3812V7.61926Z" fill="black"/>
                  <path className={styles.voicebar} d="M4.76195 5.71447C4.76195 5.45148 4.54875 5.23828 4.28576 5.23828C4.02277 5.23828 3.80957 5.45148 3.80957 5.71447V14.2859C3.80957 14.5489 4.02277 14.7621 4.28576 14.7621C4.54875 14.7621 4.76195 14.5489 4.76195 14.2859V5.71447Z" fill="black"/>
                  <path className={styles.voicebar} d="M8.57152 2.85705C8.57152 2.59406 8.35832 2.38086 8.09533 2.38086C7.83234 2.38086 7.61914 2.59406 7.61914 2.85705V17.1428C7.61914 17.4058 7.83234 17.619 8.09533 17.619C8.35832 17.619 8.57152 17.4058 8.57152 17.1428V2.85705Z" fill="black"/>
                  <path className={styles.voicebar} d="M12.3811 5.71447C12.3811 5.45148 12.1679 5.23828 11.9049 5.23828C11.6419 5.23828 11.4287 5.45148 11.4287 5.71447V14.2859C11.4287 14.5489 11.6419 14.7621 11.9049 14.7621C12.1679 14.7621 12.3811 14.5489 12.3811 14.2859V5.71447Z" fill="black"/>
                  <path className={styles.voicebar} d="M16.1907 2.85705C16.1907 2.59406 15.9775 2.38086 15.7145 2.38086C15.4515 2.38086 15.2383 2.59406 15.2383 2.85705V17.1428C15.2383 17.4058 15.4515 17.619 15.7145 17.619C15.9775 17.619 16.1907 17.4058 16.1907 17.1428V2.85705Z" fill="black"/>
                  <path className={styles.voicebar} d="M20.0002 7.61926C20.0002 7.35626 19.787 7.14307 19.524 7.14307C19.261 7.14307 19.0479 7.35626 19.0479 7.61926V12.3812C19.0479 12.6442 19.261 12.8574 19.524 12.8574C19.787 12.8574 20.0002 12.6442 20.0002 12.3812V7.61926Z" fill="black"/>
                </g>
                <defs>
                  <clipPath id="clip0_752_3283">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </i>
          )}
          <span className={styles.voiceMicStatus}>{statusText}</span>
        </button>
        
      </div>
      <ReactTooltip className={styles.customTooltip} id="voiceModel" place="top" content={tooltipText} />
    </>
  );
};
